import { useState, useEffect } from 'react';
import { useAuth } from '@qwickapps/auth-client';
import { config } from '../config/runtime';

/**
 * Shape returned by GET /api/agents (middleware-service).
 * Note: the endpoint returns agent definitions from the database.
 * There is no runtime `status` field — all registered agents are
 * treated as 'online' until a forge health endpoint is wired up.
 *
 * TODO: replace with GET /api/forge/instances (or a dedicated forge
 * status proxy on the middleware-service) to show only currently-running
 * agents with live busy/offline state.
 */
interface ApiAgent {
  id: string;
  name?: string;
  displayName?: string;
  isSystem?: boolean;
  lastSeen?: string;
  currentTask?: string;
}

export interface Agent {
  id: string;
  name: string;
  status: 'online' | 'offline' | 'busy';
  lastSeen?: string;
  currentTask?: string;
  type: 'system' | 'custom';
}

export function mapApiAgentToAgent(a: ApiAgent): Agent {
  return {
    id: a.id,
    name: a.displayName ?? a.name ?? a.id,
    // /api/agents returns definitions only; default to 'online' until a
    // live forge-status endpoint is available.
    status: 'online',
    lastSeen: a.lastSeen,
    currentTask: a.currentTask,
    type: a.isSystem ? 'system' : 'custom',
  };
}

interface UseAgentsOptions {
  skip?: boolean;
}

interface UseAgentsResult {
  agents: Agent[] | null;
  loading: boolean;
  error: string | null;
}

export function useAgents(options?: UseAgentsOptions): UseAgentsResult {
  const { session } = useAuth();
  const [agents, setAgents] = useState<Agent[] | null>(null);
  const [loading, setLoading] = useState(!options?.skip);
  const [error, setError] = useState<string | null>(null);

  const accessToken = session?.accessToken;

  useEffect(() => {
    if (options?.skip) return;
    // Skip fetch when no access token is available (user not authenticated yet).
    if (!accessToken) {
      setLoading(false);
      setAgents([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetch(`${config.apiUrl}/api/agents`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (!cancelled) {
          const raw: ApiAgent[] = Array.isArray(data) ? data : (data.agents ?? []);
          setAgents(raw.map(mapApiAgentToAgent));
          setError(null);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load agents');
          setAgents([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [options?.skip, accessToken]);

  return { agents, loading, error };
}
